import { Box, Button, Card, CopyButton, Grid, Group, SegmentedControl, Text, TextInput } from '@mantine/core';
import { IconCopy, IconDeviceFloppy, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import ISO6391 from 'iso-639-1';
import { surveyLocalization } from 'survey-core';
import * as SurveyTheme from 'survey-core/themes';
import { SurveyCreator, SurveyCreatorComponent } from 'survey-creator-react';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import * as LegacyThemeFamilies from './legacyThemeFamilies';

ISO6391.getLanguages(ISO6391.getAllCodes()).forEach(({ code, name }) => {
  surveyLocalization.localeNames[code] = name;
});
surveyLocalization.supportedLocales = ISO6391.getAllCodes();

const creator = new SurveyCreator({
  showLogicTab: true,
  showThemeTab: true,
  showTranslationTab: true,
  isAutoSave: true,
});

// SurveyTheme is v3's registry of built-in families. LegacyThemeFamilies restores the
// four families (Sharp, DoubleBorder, Layered, Solid) v3 removed, so a survey stored
// on one of them still matches a registered theme instead of silently rewriting to
// "default" the next time the Theme tab is opened. See legacyThemeFamilies.js.
[...Object.values(SurveyTheme), ...Object.values(LegacyThemeFamilies)].forEach((theme) => {
  if (theme && typeof theme === 'object' && theme.themeName) {
    creator.themeEditor.addTheme(theme);
  }
});

const SurveyForm = ({ survey: passedSurvey, deleteSurvey, saveSurvey, cancel }) => {
  const { accountId, domainId } = useParams();

  const navigate = useNavigate();
  const fetch = useFetch();
  const [error, setError] = useState();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [survey, setSurvey] = useState(passedSurvey);

  creator.saveSurveyFunc = (saveNo, cb) => {
    try {
      if (!survey.saveNo || saveNo > survey.saveNo) {
        setSurvey({ ...survey, saveNo, config: creator.JSON });
        cb(saveNo, true);
      }
    } catch (e) {
      console.error(e);
      cb(saveNo, false);
    }
  };

  creator.saveThemeFunc = (saveNo, cb) => {
    try {
      if (!survey.saveNo || saveNo > survey.saveNo) {
        setSurvey({ ...survey, saveNo, theme: creator.theme });
        cb(saveNo, true);
      }
    } catch (e) {
      console.error(e);
      cb(saveNo, false);
    }
  };

  useEffect(() => {
    if (passedSurvey?.config) {
      creator.text = JSON.stringify(passedSurvey.config);
    }
    if (passedSurvey?.theme && Object.keys(passedSurvey?.theme).length > 0) {
      creator.theme = passedSurvey.theme;
    }
  }, [passedSurvey?.config, passedSurvey?.theme]);

  const create = (survey) => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pages/${survey.pageId}/surveys`, {
      method: 'POST',
      body: JSON.stringify({ ...survey, templateId: survey.templateId || null }),
    })
      .then(({ survey }) => {
        navigate(`/accounts/${accountId}/domains/${domainId}/pages?type=surveys&page=${survey.pageId}`);
      })
      .catch(setError);
  };

  if (survey === null) {
    return <ErrorMessage message="Survey does not exist!" />;
  }

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  let deleteButton;
  if (typeof deleteSurvey === 'function') {
    deleteButton = (
      <LockableButton restrictionKey="surveysDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
        <IconTrash size={14} /> Delete
      </LockableButton>
    );
  }

  let copyButton;
  if (survey.id) {
    copyButton = (
      <LockableButton
        restrictionKey="surveysCreate"
        color="cyan"
        onClick={() => navigate(`/accounts/${accountId}/domains/${domainId}/surveys/new?from=${survey.id}`)}
      >
        <IconCopy size={14} /> Make a copy
      </LockableButton>
    );
  }

  // publicUrl is computed by the API, so it only exists on a saved survey. "Make a copy"
  // hands the form the source survey without its id, so gate on the id too - otherwise the
  // unsaved copy would advertise the original survey's link. The else-if covers every other
  // case the flag can be on without a link to show: an unsaved survey, and the ordinary edit
  // path where an existing survey just had the flag turned on and the API has not minted its
  // publicUrl yet.
  let publicLink;
  if (survey.allowAnonymousAccess && survey.id && survey.publicUrl) {
    publicLink = (
      <Group gap="sm" mt="xs" align="center">
        <TextInput readOnly value={survey.publicUrl} aria-label="Public survey link" style={{ flex: 1 }} />
        <CopyButton value={survey.publicUrl}>
          {({ copied, copy }) => (
            <Button size="xs" variant="default" onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          )}
        </CopyButton>
      </Group>
    );
  } else if (survey.allowAnonymousAccess) {
    publicLink = (
      <Text size="sm" c="dimmed" mt="xs">
        The public link appears here once you save the survey.
      </Text>
    );
  }

  return (
    <>
      <Card>
        <Box ta="left">
          <Grid data-tour="productFeedName">
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Text size="sm" fw={500}>
                Name*
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <TextInput
                name="name"
                type="text"
                value={survey.name}
                placeholder="e.g. Tell us about your last order"
                onChange={(e) => setSurvey({ ...survey, [e.target.name]: e.target.value })}
              />
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }}>Allowed Submissions*</Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <SegmentedControl
                value={survey.config.cookieName ?? ''}
                onChange={(cookieName) =>
                  setSurvey({ ...survey, config: { ...survey.config, cookieName: cookieName || undefined } })
                }
                data={[
                  { label: 'Once per link', value: 'rlv_survey_submitted' },
                  // SegmentedControl only carries string values, so "no cookie" round-trips as ''.
                  { label: 'Multiple times', value: '' },
                ]}
              />
              <Box size="sm" c="dimmed" fz="sm" lh="sm">
                <Text size="sm" c="dimmed">
                  <strong>Once per link</strong> - if a user receives a survey link for a purchase on Monday, and for
                  another purchase on Wednesday, they can fill out this survey once for each link sent, or twice in
                  total.
                </Text>
                <Text size="sm" c="dimmed">
                  <strong>Multiple times</strong> means that the survey can be submitted multiple times for each link
                  sent.
                </Text>
                {survey.allowAnonymousAccess ? (
                  <Text size="sm" c="dimmed">
                    With the public link on there is no personal link to count against, so{' '}
                    <strong>Once per link</strong> can only stop a repeat submission from the same browser.
                  </Text>
                ) : null}
              </Box>
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }}>Public link (QR code)</Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <SegmentedControl
                value={String(survey.allowAnonymousAccess ?? false)}
                onChange={(value) => setSurvey({ ...survey, allowAnonymousAccess: value === 'true' })}
                data={[
                  { label: 'Yes', value: 'true' },
                  { label: 'No', value: 'false' },
                ]}
                aria-label="Public link (QR code)"
              />
              <Text size="sm" c="dimmed">
                <strong>Yes</strong> - anyone holding the link can open and submit this survey, no personal link needed,
                so you can print it as a QR code and put it up in your stores. Respondents are anonymous unless they
                choose to identify themselves: add an email or phone-number question to the survey and turn on the
                matching "Merge ... Into Profile" setting below, and that answer ties the submission to the customer's
                profile. Leaving it blank is fine - the submission is still recorded.
              </Text>
              {publicLink}
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }}>Merge Email Into Profile</Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <SegmentedControl
                value={String(survey.mergeEmailIntoProfile ?? false)}
                onChange={(value) => setSurvey({ ...survey, mergeEmailIntoProfile: value === 'true' })}
                data={[
                  { label: 'Yes', value: 'true' },
                  { label: 'No', value: 'false' },
                ]}
                aria-label="Merge Email Into Profile"
              />
              <Text size="sm" c="dimmed">
                The answer to the first email question in this survey is saved as the profile's email address, replacing
                any address already on the profile.
              </Text>
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }}>Merge Phone Number Into Profile</Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <SegmentedControl
                value={String(survey.mergePhoneNumberIntoProfile ?? false)}
                onChange={(value) => setSurvey({ ...survey, mergePhoneNumberIntoProfile: value === 'true' })}
                data={[
                  { label: 'Yes', value: 'true' },
                  { label: 'No', value: 'false' },
                ]}
                aria-label="Merge Phone Number Into Profile"
              />
              <Text size="sm" c="dimmed">
                The answer to the first phone-number question in this survey is saved as the profile's phone number,
                replacing any number already on the profile.
              </Text>
            </Grid.Col>
          </Grid>
          <hr />

          {/* isolation keeps the creator's internal z-indexes (toolbox flyout: 200) local, so
              the fixed app sidebar (z-index 100) still paints over it. SurveyJS popups mount
              on document.body, so they are unaffected. */}
          <Box style={{ height: '90vh', isolation: 'isolate' }} mt="md">
            <SurveyCreatorComponent creator={creator} />
          </Box>

          <Box>
            <Group justify="flex-end" gap="xs" py="md">
              <Button variant="default" onClick={() => cancel()}>
                Cancel
              </Button>
              {deleteButton}
              {copyButton}
              <LockableButton
                restrictionKey={`surveys${survey.id ? 'Update' : 'Create'}`}
                disabled={!survey.name || !survey.config}
                onClick={() => (saveSurvey ? saveSurvey(survey) : create(survey))}
                data-tour="saveBtnSurvey"
              >
                <IconDeviceFloppy size={14} /> Save
              </LockableButton>
            </Group>
          </Box>
        </Box>
        <ConfirmationModal
          show={confirmDeleteOpen}
          close={() => setConfirmDeleteOpen(false)}
          confirm={(cb) => deleteSurvey(cb)}
          message="This action cannot be undone!"
        />
      </Card>
    </>
  );
};

export default SurveyForm;
